'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn';
import type { FilterConfig, FilterValue } from './data-table-types';

interface DataTableFilterProps {
  columnId: string;
  columnLabel: string;
  config: FilterConfig;
  value: FilterValue | undefined;
  onChange: (value: FilterValue | undefined) => void;
  align?: 'left' | 'right';
}

/**
 * Dropdown de filtrare pentru o coloană. Variante: text, number-range, select,
 * multi-select, date-range. Încapsulează ARIA (role="dialog", aria-haspopup,
 * aria-expanded) și outside-click + Escape close.
 */
export function DataTableFilter({ columnId, columnLabel, config, value, onChange, align = 'left' }: DataTableFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasValue = isFilterActive(value);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Filtrează coloana ${columnLabel}`}
        className={cn(
          'inline-grid place-items-center rounded p-1 transition-colors',
          hasValue
            ? 'bg-primary-500/15 text-primary-600 opacity-100'
            : 'opacity-60 text-neutral-500 hover:bg-neutral-100',
          open && 'bg-primary-500/15',
        )}
        title="Filtru"
      >
        <FilterIcon active={hasValue} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={`Filtru ${columnLabel}`}
          className={cn(
            'absolute top-[calc(100%+4px)] z-30 min-w-[220px] rounded-lg border border-neutral-200 bg-white p-3 shadow-xl',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FilterBody columnId={columnId} columnLabel={columnLabel} config={config} value={value} onChange={onChange} onClose={() => setOpen(false)} />
          {hasValue && (
            <button
              type="button"
              onClick={() => { onChange(undefined); setOpen(false); }}
              className="mt-2 w-full rounded border border-neutral-200 bg-transparent px-2 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
            >
              Șterge filtrul
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FilterBody({ columnId, columnLabel, config, value, onChange, onClose }: DataTableFilterProps & { onClose: () => void }) {
  if (config.type === 'text') {
    return (
      <label className="block">
        <span className="sr-only">{`Caută în ${columnLabel}`}</span>
        <input
          autoFocus
          type="search"
          placeholder={config.placeholder ?? `Caută ${columnLabel.toLowerCase()}...`}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }}
          className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm text-neutral-900 outline-none focus:border-primary-500"
        />
      </label>
    );
  }

  if (config.type === 'number-range') {
    const v = (value as { min?: number; max?: number }) ?? {};
    const update = (patch: Partial<typeof v>) => {
      const next = { ...v, ...patch };
      const cleaned: typeof v = {};
      if (next.min !== undefined && !isNaN(Number(next.min))) cleaned.min = Number(next.min);
      if (next.max !== undefined && !isNaN(Number(next.max))) cleaned.max = Number(next.max);
      onChange(cleaned.min === undefined && cleaned.max === undefined ? undefined : cleaned);
    };
    return (
      <div className="flex gap-2">
        <label className="flex-1">
          <span className="sr-only">{`${columnLabel} minim`}</span>
          <input
            type="number"
            placeholder="Min"
            value={v.min ?? ''}
            onChange={(e) => update({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm text-neutral-900 outline-none focus:border-primary-500"
          />
        </label>
        <label className="flex-1">
          <span className="sr-only">{`${columnLabel} maxim`}</span>
          <input
            type="number"
            placeholder="Max"
            value={v.max ?? ''}
            onChange={(e) => update({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm text-neutral-900 outline-none focus:border-primary-500"
          />
        </label>
      </div>
    );
  }

  if (config.type === 'select') {
    return (
      <div className="flex flex-col gap-1">
        {config.options?.map((opt) => {
          const active = (value as string) === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(active ? undefined : opt.value); onClose(); }}
              className={cn(
                'rounded px-2 py-1.5 text-left text-sm transition-colors',
                active ? 'bg-primary-50 font-medium text-primary-700' : 'text-neutral-700 hover:bg-neutral-100',
              )}
            >
              {active ? '● ' : '○ '}{opt.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (config.type === 'multi-select') {
    const selected = new Set((value as string[]) ?? []);
    const toggle = (v: string) => {
      const next = new Set(selected);
      if (next.has(v)) next.delete(v); else next.add(v);
      onChange(next.size === 0 ? undefined : Array.from(next));
    };
    return (
      <div className="flex flex-col gap-1">
        {config.options?.map((opt) => {
          const active = selected.has(opt.value);
          return (
            <label key={opt.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-neutral-100">
              <input type="checkbox" checked={active} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          );
        })}
      </div>
    );
  }

  if (config.type === 'date-range') {
    const v = (value as { from?: string; to?: string }) ?? {};
    const update = (patch: Partial<typeof v>) => {
      const next = { ...v, ...patch };
      onChange((next.from || next.to) ? next : undefined);
    };
    return (
      <div className="flex flex-col gap-2">
        <label>
          <span className="block text-xs font-semibold text-neutral-500">De la</span>
          <input
            type="date"
            value={v.from ?? ''}
            onChange={(e) => update({ from: e.target.value || undefined })}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm outline-none focus:border-primary-500"
          />
        </label>
        <label>
          <span className="block text-xs font-semibold text-neutral-500">Până la</span>
          <input
            type="date"
            value={v.to ?? ''}
            onChange={(e) => update({ to: e.target.value || undefined })}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm outline-none focus:border-primary-500"
          />
        </label>
      </div>
    );
  }

  return null;
}

function FilterIcon({ active }: { active: boolean }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export function isFilterActive(v: FilterValue | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    return Object.values(obj).some((x) => x !== undefined && x !== '' && x !== null);
  }
  return false;
}

export function applyFilter(value: unknown, filterValue: FilterValue, type: FilterConfig['type']): boolean {
  if (!isFilterActive(filterValue)) return true;
  if (type === 'text') {
    return String(value ?? '').toLowerCase().includes(String(filterValue).toLowerCase());
  }
  if (type === 'number-range') {
    const n = Number(value);
    const r = filterValue as { min?: number; max?: number };
    if (r.min !== undefined && n < r.min) return false;
    if (r.max !== undefined && n > r.max) return false;
    return true;
  }
  if (type === 'select') {
    return String(value) === String(filterValue);
  }
  if (type === 'multi-select') {
    return (filterValue as string[]).includes(String(value));
  }
  if (type === 'date-range') {
    const r = filterValue as { from?: string; to?: string };
    const d = String(value);
    if (r.from && d < r.from) return false;
    if (r.to && d > r.to) return false;
    return true;
  }
  return true;
}
